import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/hub/lib/api";
import { ChevronLeft, ChevronRight, MapPin, Mountain, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BookingSheet } from "@/hub/components/BookingSheet";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarView() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [reservations, setReservations] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [zones, setZones] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activities, setActivities] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const load = () => {
    const start = fmt(new Date(year, month, 1));
    const end = fmt(new Date(year, month + 1, 0));
    Promise.all([api.reservations(start, end), api.scheduled(start, end)]).then(([r, s]) => {
      setReservations(r);
      setScheduled(s);
    });
  };

  useEffect(() => {
    load();
  }, [year, month]);
  useEffect(() => {
    api.zones().then(setZones);
    api.groups().then(setGroups);
    api.activities().then(setActivities);
    api.packages().then(setPackages);
  }, []);

  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    let startDay = first.getDay() - 1;
    if (startDay < 0) startDay = 6;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  }, [year, month]);

  const eventsFor = (date) => {
    const ds = fmt(date);
    return {
      res: reservations.filter((r) => r.date === ds),
      act: scheduled.filter((a) => a.date === ds),
    };
  };

  const selectedEvents = selectedDay ? eventsFor(new Date(selectedDay + "T00:00:00")) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-3 bg-white border border-border">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-heading text-2xl font-medium tracking-tight">
            {cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </h2>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="rounded-sm h-8 w-8" data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm h-8" data-testid="cal-today" onClick={() => setCursor(new Date())}>Today</Button>
            <Button variant="outline" size="icon" className="rounded-sm h-8 w-8" data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-7 border-b border-border">
          {DAYS.map((d) => (
            <div key={d} className="text-center text-xs font-semibold uppercase tracking-wider text-stone-400 py-2 border-r border-border last:border-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((date, i) => {
            if (!date) return <div key={i} className="min-h-[104px] border-r border-b border-border bg-stone-50/50" />;
            const { res, act } = eventsFor(date);
            const ds = fmt(date);
            const isToday = ds === fmt(new Date());
            return (
              <button
                key={i}
                data-testid={`cal-day-${ds}`}
                onClick={() => setSelectedDay(ds)}
                className={`min-h-[104px] border-r border-b border-border last:border-r-0 p-1.5 text-left align-top hover:bg-stone-50 transition-colors ${selectedDay === ds ? "bg-forest/5 ring-1 ring-inset ring-forest" : ""}`}
              >
                <div className={`text-xs font-mono mb-1 h-5 w-5 flex items-center justify-center ${isToday ? "bg-terracotta text-white rounded-sm" : "text-stone-500"}`}>{date.getDate()}</div>
                <div className="space-y-0.5">
                  {res.slice(0, 2).map((r) => (
                    <div key={r.id} onClick={(e) => { e.stopPropagation(); navigate(`/hub/groups/${r.group_id}`); }} className="text-[10px] px-1 py-0.5 rounded-sm text-white truncate leading-tight cursor-pointer hover:opacity-80" style={{ background: r.zone_color }} title={`${r.zone_name} · ${r.group_name} — view client`} data-testid={`cal-res-${r.id}`}>
                      {r.zone_name.replace(" Zone", "")} · {r.group_name}
                    </div>
                  ))}
                  {act.slice(0, 1).map((a) => (
                    <div key={a.id} className="text-[10px] px-1 py-0.5 rounded-sm bg-stone-100 text-stone-600 truncate leading-tight border-l-2 border-terracotta">
                      {a.time} {a.activity_name}
                    </div>
                  ))}
                  {res.length + act.length > 3 && (
                    <div className="text-[10px] text-stone-400 px-1">+{res.length + act.length - 3} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-border p-5 h-fit lg:sticky lg:top-24">
        {!selectedDay ? (
          <div className="text-center text-sm text-stone-400 py-10 border border-dashed border-stone-200 rounded-sm">Select a day to view & schedule reservations and activities.</div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium">{new Date(selectedDay + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "short" })}</h3>
              <button onClick={() => setSelectedDay(null)} data-testid="close-day-panel"><X className="h-4 w-4 text-stone-400 hover:text-stone-700" /></button>
            </div>

            <div className="flex gap-2 mb-4">
              <BookingSheet defaultDate={selectedDay} groups={groups} packages={packages} activities={activities} onDone={load}
                trigger={<Button size="sm" className="rounded-sm bg-forest hover:bg-forest-hover w-full" data-testid="open-add-booking"><Plus className="h-4 w-4 mr-1" /> Create Booking</Button>} />
            </div>

            <div className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-400 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Zone Reservations</div>
              {selectedEvents.res.length === 0 && <p className="text-xs text-stone-400">None reserved.</p>}
              {selectedEvents.res.map((r) => (
                <div key={r.id} className="flex items-center justify-between border border-border p-2.5 rounded-sm group" data-testid={`res-item-${r.id}`}>
                  <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(`/hub/groups/${r.group_id}`)} data-testid={`res-view-${r.id}`}>
                    <span className="h-8 w-1 rounded-sm" style={{ background: r.zone_color }} />
                    <div>
                      <div className="text-sm font-medium hover:text-forest">{r.zone_name}</div>
                      <div className="text-xs text-stone-400">{r.group_name} · {r.pax} pax</div>
                    </div>
                  </div>
                  <button onClick={() => api.deleteReservation(r.id).then(() => { toast.success("Reservation removed"); load(); })} data-testid={`del-res-${r.id}`}>
                    <Trash2 className="h-4 w-4 text-stone-300 hover:text-red-500 transition-colors" />
                  </button>
                </div>
              ))}

              <div className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-400 flex items-center gap-1.5 pt-2"><Mountain className="h-3.5 w-3.5" /> Scheduled Activities</div>
              {selectedEvents.act.length === 0 && <p className="text-xs text-stone-400">None scheduled.</p>}
              {selectedEvents.act.map((a) => (
                <div key={a.id} className="flex items-center justify-between border border-border p-2.5 rounded-sm border-l-2 border-l-terracotta" data-testid={`act-item-${a.id}`}>
                  <div>
                    <div className="text-sm font-medium">{a.activity_name}</div>
                    <div className="text-xs text-stone-400 font-mono">{a.time} · {a.group_name}</div>
                  </div>
                  <button onClick={() => api.deleteScheduled(a.id).then(() => { toast.success("Activity removed"); load(); })} data-testid={`del-act-${a.id}`}>
                    <Trash2 className="h-4 w-4 text-stone-300 hover:text-red-500 transition-colors" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
